import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef } from 'react';

import { EVENTS } from '@apps/web/lib/events';

/**
 * Product analytics for Teach Phoebe (training mode).
 *
 * The training-mode dashboard measures whether training produces durable
 * notes; these events add the interaction side of that loop (page visit →
 * run opened → run reviewed) that backend note events cannot see. One hook
 * owns the payload contract so every surface that reviews training runs
 * (Teach Phoebe page, chat sidebar, home training card) describes the same
 * interaction identically. Surfaces are told apart in analysis by the
 * auto-captured $pathname rather than a threaded prop.
 *
 * Every capture is best-effort: analytics must never break the surface it
 * measures.
 */

type RunReviewedEvent = {
    readonly channel: string;
    // 'cleared' is an unclick: the coordinator withdrew the rating.
    readonly label: 'good' | 'bad' | 'cleared';
    readonly hasComment: boolean;
};

type TeachPhoebeAnalytics = {
    readonly captureRunOpened: (event: { readonly channel: string }) => void;
    readonly captureRunReviewed: (event: RunReviewedEvent) => void;
};

export function useTeachPhoebeAnalytics(
    organizationId: string | null,
): TeachPhoebeAnalytics {
    const posthog = usePostHog();

    // Stable identities so consumers can list these in useCallback deps
    // without re-creating their own callbacks every render.
    const capture = useCallback(
        (eventName: string, properties: Record<string, unknown>): void => {
            try {
                posthog?.capture(eventName, {
                    organization_id: organizationId,
                    ...properties,
                });
            } catch {
                // Best-effort: a failed capture must not break the
                // interaction.
            }
        },
        [organizationId, posthog],
    );

    return {
        captureRunOpened: useCallback(
            (event) => {
                capture(EVENTS.TRAIN_PHOEBE_RUN_OPENED, {
                    channel: event.channel,
                });
            },
            [capture],
        ),
        captureRunReviewed: useCallback(
            (event) => {
                capture(EVENTS.TRAIN_PHOEBE_RUN_REVIEWED, {
                    channel: event.channel,
                    label: event.label,
                    has_comment: event.hasComment,
                });
            },
            [capture],
        ),
    };
}

/**
 * Fires once per organization per mount, only once the org is confirmed to
 * be in training mode (trainingDay resolved).
 *
 * An explicit event, not `$pageview`: PostHog runs with the legacy pageview
 * behavior (a pageview only on initial document load), and this page is
 * reached by client-side route links — so relying on `$pageview` would miss
 * almost every visit.
 */
export function useTeachPhoebePageViewed({
    organizationId,
    trainingDay,
}: {
    readonly organizationId: string | null;
    readonly trainingDay: number | null;
}): void {
    const posthog = usePostHog();
    const capturedForOrganizationRef = useRef<string | null>(null);

    useEffect(() => {
        if (
            organizationId === null ||
            trainingDay === null ||
            capturedForOrganizationRef.current === organizationId
        ) {
            return;
        }
        capturedForOrganizationRef.current = organizationId;
        try {
            posthog?.capture(EVENTS.TRAIN_PHOEBE_PAGE_VIEWED, {
                organization_id: organizationId,
                training_day: trainingDay,
            });
        } catch {
            // Best-effort.
        }
    }, [organizationId, posthog, trainingDay]);
}
